import { FC } from 'react'

import styled from 'styled-components'
import { IncidentsReportMain } from './components'
import { IncidentsDashboard } from './components'
import { useTranslation } from 'react-i18next'
import { incidentReportsConstants } from 'locale/incidentReports'
import { NAMESPACE } from 'locale'
import { useSelector } from 'react-redux'
import { Home, UserRole } from '@lmc/ts-common'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useRoleAccess } from 'components/AccessControl'
import { EarlyAccessBanner, theme } from '@lmc/ui-kit'

type Props = {
  hasResidentsFilter?: boolean
  hasHomesFilter?: boolean
  hasOnClick?: boolean
}

export const IncidentsReportMainScreen: FC<Props> = ({
  hasResidentsFilter,
  hasHomesFilter,
  hasOnClick = true,
}) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)

  const params = useParams()

  const isListView = params?.report === 'list'

  const isDashboardView = params?.report === 'dashboard'

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home,
  )

  const proEnabled = home?.features?.pro?.enabled

  const flags = useFeatureFlags({
    flagNames: [
      'incident_reports_list_view',
      'incidents_dashboard',
      'incident_case_action_assignment',
    ],
  })?.data

  const navigate = useNavigate()

  const hasRolePermission = useRoleAccess(
    [UserRole.careHomeAdmin],
    [
      {
        i: 'update',
        this: 'Incident',
      },
    ],
  )

  const incidentReportsListViewEnabled =
    flags.incident_reports_list_view.enabled

  const incidentsDashboardEnabled = flags.incidents_dashboard.enabled

  if (
    hasOnClick &&
    !incidentReportsListViewEnabled &&
    !incidentsDashboardEnabled
  ) {
    return null
  }

  const actionsFlag = flags.incident_case_action_assignment.enabled
  const displayBanner =
    actionsFlag && home.features?.pro?.enabled && hasRolePermission

  if (hasOnClick && !proEnabled) {
    navigate('/')
  }

  return (
    <Wrapper>
      {displayBanner ? (
        <EarlyAccessBanner
          title="Action Owners"
          color={theme.color.category.foodAndDrink}
          description={
            <Link
              style={{
                color: theme.color.neutral.color11,
                textDecoration: 'underline',
              }}
              to="/reports/incidents/actions"
            >
              Add owners to all incident case actions you create!
            </Link>
          }
        />
      ) : null}
      {isListView && incidentReportsListViewEnabled && (
        <IncidentsReportMain
          title={t(incidentReportsConstants.INCIDENTS_LIST_VIEW)}
          hasResidentsFilter={hasResidentsFilter}
          hasHomesFilter={hasHomesFilter}
          hasOnClick={hasOnClick}
        />
      )}
      {isDashboardView && incidentsDashboardEnabled && (
        <IncidentsDashboard
          title={t(incidentReportsConstants.INCIDENTS_LIST_VIEW)}
          hasResidentsFilter={hasResidentsFilter}
          hasHomesFilter={hasHomesFilter}
          hasOnClick={hasOnClick}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  max-width: 1480px;
  padding: 24px 0 50px;
  margin: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${({ theme }) => theme.mediaQueries?.tabletDown} {
    padding: 24px;
  }

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin: 0;
  }
`
